import { useClaims } from '@backoffice-frontend/authentication'
import { getLanguage } from '@backoffice-frontend/common'
import { useErrorHandlingQuery } from '@backoffice-frontend/data-fetching'
import { Divider } from '@backoffice-frontend/patterns'
import {
  TenantSection,
  useBackofficeLoggedInUser,
} from '@backoffice-frontend/user-management-ui'
import { Accordion, LoadingIndicator, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import type { AssignedOrganizationsViewQuery } from './AssignedOrganizationsView.hook'
import { useAssignedOrganizationsViewQuery } from './AssignedOrganizationsView.hook'
import { AccountLayout } from './components/AccountLayout'

export const AssignedOrganizationsView = () => {
  const { t, i18n } = useTranslation()
  const { user } = useBackofficeLoggedInUser()
  const claims = useClaims()

  const { data, loading } = useErrorHandlingQuery(
    useAssignedOrganizationsViewQuery,
    {
      variables: { email: claims.cognito.email() },
    },
  )

  const adminPanelOrgIds = user?.adminPanelOrganizations ?? []
  const organizations = data?.organizations.filter(org =>
    adminPanelOrgIds.includes(org.id),
  )

  const userFleets = data?.fleets ?? []
  const userServiceAreaIds = claims.boum.serviceAreas()
  const allServiceAreas = data?.serviceAreas ?? []
  const filteredServiceAreas = allServiceAreas.filter(it =>
    userServiceAreaIds.includes(it.uuid),
  )

  return (
    <AccountLayout
      header={t('authenticatedAppView_assignedOrganizations_header')}
    >
      {loading ? (
        <div css={{ display: 'flex', justifyContent: 'center' }}>
          <LoadingIndicator />
        </div>
      ) : organizations?.length === 0 ? (
        <Text>{t('authenticatedAppView_noAssignedOrganizations_label')}</Text>
      ) : (
        <div>
          {organizations?.map(org => (
            <OrganizationAccordion
              key={org.id}
              name={org.name}
              ridepoolingServiceTenants={org.ridepoolingServiceTenants}
              customerTenants={org.customerTenants}
              operatorTenants={org.operatorTenants}
              fleets={userFleets.filter(fleet =>
                org.operatorTenants.includes(fleet.operatorTenant),
              )}
              serviceAreas={filteredServiceAreas.filter(sa =>
                sa.operatorTenants.some(tenant =>
                  org.operatorTenants.includes(tenant),
                ),
              )}
              language={i18n.language}
            />
          ))}
        </div>
      )}
    </AccountLayout>
  )
}

type OrganizationItem = AssignedOrganizationsViewQuery['organizations'][number]
type FleetItem = NonNullable<AssignedOrganizationsViewQuery['fleets']>[number]
type ServiceAreaItem = AssignedOrganizationsViewQuery['serviceAreas'][number]

const OrganizationAccordion = ({
  name,
  ridepoolingServiceTenants,
  customerTenants,
  operatorTenants,
  fleets,
  serviceAreas,
  language,
}: {
  name: string
  ridepoolingServiceTenants: OrganizationItem['ridepoolingServiceTenants']
  customerTenants: OrganizationItem['customerTenants']
  operatorTenants: OrganizationItem['operatorTenants']
  fleets: FleetItem[]
  serviceAreas: ServiceAreaItem[]
  language: string
}) => {
  const { t } = useTranslation()

  const allSections = [
    {
      label: t('authenticatedAppView_ridepoolingServiceTenants_label'),
      tenants: ridepoolingServiceTenants,
    },
    {
      label: t('authenticatedAppView_customerTenants_label'),
      tenants: customerTenants,
    },
    {
      label: t('authenticatedAppView_operatorTenants_label'),
      tenants: operatorTenants,
    },
    ...(fleets.length > 0
      ? [
          {
            label: t('authenticatedAppView_fleets_label'),
            tenants: fleets.map(f => f.name),
          },
        ]
      : []),
    ...(serviceAreas.length > 0
      ? [
          {
            label: t('authenticatedAppView_serviceAreas_label'),
            tenants: serviceAreas.map(
              sa => sa.displayNames[getLanguage(language)] ?? sa.id,
            ),
          },
        ]
      : []),
  ].filter(section => section.tenants.length > 0)

  return (
    <Accordion title={name} compact>
      <div
        css={theme => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.space.Space2,
        })}
      >
        {allSections.map((section, index) => (
          <div key={section.label}>
            <TenantSection label={section.label} tenants={section.tenants} />
            {index < allSections.length - 1 && (
              <Divider css={theme => ({ marginTop: theme.space.Space2 })} />
            )}
          </div>
        ))}
      </div>
    </Accordion>
  )
}
